'use client';

import { useState } from 'react';
import {
  ArrowRight,
  ArrowRightLeft,
  CreditCard,
  Download,
  ExternalLink,
  Loader2,
  Wallet,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { useStoreNavigate } from '@/hooks/use-app-navigate';
import { trpc } from '@/lib/api/trpc';

export const WOO_CHECKOUT_PLUGINS_DOCS =
  'https://docs.tagada.io/developer-tools/woocommerce/checkout-plugins';

function downloadBase64File(content: string, filename: string, mimeType: string) {
  const bytes = Uint8Array.from(window.atob(content), (c) => c.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function WooCommerceCheckoutPlugins({ storeId }: { storeId?: string }) {
  const { t } = useTranslation('integrations');
  const { toast } = useToast();
  const { storeRoutes, navigate } = useStoreNavigate();
  const generateScript = trpc.woocommerceScript.generateScript.useMutation();
  const [downloading, setDownloading] = useState(false);

  const handleDownloadInline = async () => {
    if (!storeId) {
      toast({
        title: t('woocommerce.checkoutPlugins.inline.failed'),
        description: t('woocommerce.errors.missingIds'),
        variant: 'destructive',
      });
      return;
    }
    setDownloading(true);
    try {
      const res = await generateScript.mutateAsync({
        storeId,
        funnelId: '',
        stepId: '',
        format: 'inline',
      });
      if (!res?.content) {
        throw new Error(t('woocommerce.checkoutPlugins.inline.failed'));
      }
      downloadBase64File(res.content, res.filename, res.mimeType);
      toast({ title: t('woocommerce.checkoutPlugins.inline.downloaded') });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('woocommerce.checkoutPlugins.inline.failed');
      toast({
        title: t('woocommerce.checkoutPlugins.inline.failed'),
        description: message,
        variant: 'destructive',
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{t('woocommerce.checkoutPlugins.title')}</CardTitle>
            <CardDescription>{t('woocommerce.checkoutPlugins.description')}</CardDescription>
          </div>
          <a
            href={WOO_CHECKOUT_PLUGINS_DOCS}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 hover:underline dark:text-blue-300"
          >
            {t('woocommerce.checkoutPlugins.docs')}
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-muted-foreground text-xs">{t('woocommerce.checkoutPlugins.installExactlyOne')}</p>

        <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
          <table className="w-full min-w-[36rem] text-left text-xs">
            <thead className="bg-slate-50 text-slate-600 dark:bg-slate-800/80 dark:text-slate-300">
              <tr>
                <th className="px-3 py-2 font-medium">{t('woocommerce.checkoutPlugins.matrix.plugin')}</th>
                <th className="px-3 py-2 font-medium">{t('woocommerce.checkoutPlugins.matrix.installFrom')}</th>
                <th className="px-3 py-2 font-medium">{t('woocommerce.checkoutPlugins.matrix.checkout')}</th>
                <th className="px-3 py-2 font-medium">{t('woocommerce.checkoutPlugins.matrix.amount')}</th>
                <th className="px-3 py-2 font-medium">{t('woocommerce.checkoutPlugins.matrix.funnels')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              <tr className="bg-emerald-50/60 dark:bg-emerald-950/20">
                <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-100">
                  {t('woocommerce.setupGuide.inline.title')}
                </td>
                <td className="px-3 py-2 font-medium text-emerald-800 dark:text-emerald-300">
                  {t('woocommerce.checkoutPlugins.matrix.inlineFrom')}
                </td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                  {t('woocommerce.checkoutPlugins.matrix.inlineCheckout')}
                </td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                  {t('woocommerce.checkoutPlugins.matrix.wooTotal')}
                </td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                  {t('woocommerce.checkoutPlugins.matrix.no')}
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-100">
                  {t('woocommerce.setupGuide.gateway.title')}
                </td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                  {t('woocommerce.checkoutPlugins.matrix.funnelFrom')}
                </td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                  {t('woocommerce.checkoutPlugins.matrix.gatewayCheckout')}
                </td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                  {t('woocommerce.checkoutPlugins.matrix.tagadaCatalogue')}
                </td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                  {t('woocommerce.checkoutPlugins.matrix.limited')}
                </td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-100">
                  {t('woocommerce.setupGuide.redirect.title')}
                </td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                  {t('woocommerce.checkoutPlugins.matrix.funnelFrom')}
                </td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                  {t('woocommerce.checkoutPlugins.matrix.redirectCheckout')}
                </td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                  {t('woocommerce.checkoutPlugins.matrix.tagadaCatalogue')}
                </td>
                <td className="px-3 py-2 text-slate-600 dark:text-slate-400">
                  {t('woocommerce.checkoutPlugins.matrix.yes')}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-4 dark:border-emerald-800 dark:bg-emerald-950/20">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 dark:bg-emerald-900/40">
              <Wallet className="h-4 w-4 text-emerald-700 dark:text-emerald-300" />
            </div>
            <div className="min-w-0 flex-1 space-y-3">
              <div>
                <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                  {t('woocommerce.setupGuide.inline.title')}
                </p>
                <p className="text-muted-foreground mt-0.5 text-xs">
                  {t('woocommerce.setupGuide.inline.description')}
                </p>
                <p className="mt-1 text-[11px] font-medium text-emerald-800 dark:text-emerald-300">
                  {t('woocommerce.checkoutPlugins.inline.installHere')}
                </p>
              </div>
              <Button
                type="button"
                className="gap-2"
                onClick={handleDownloadInline}
                disabled={downloading || generateScript.isPending || !storeId}
              >
                {downloading || generateScript.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                {t('woocommerce.checkoutPlugins.inline.download')}
              </Button>
              <div className="text-muted-foreground space-y-1 text-xs">
                <p className="font-medium text-slate-700 dark:text-slate-300">
                  {t('woocommerce.checkoutPlugins.inline.howTitle')}
                </p>
                <ol className="list-decimal space-y-1 pl-4">
                  <li>{t('woocommerce.checkoutPlugins.inline.step1')}</li>
                  <li>{t('woocommerce.checkoutPlugins.inline.step2')}</li>
                  <li>{t('woocommerce.checkoutPlugins.inline.step3')}</li>
                  <li>{t('woocommerce.checkoutPlugins.inline.step4')}</li>
                </ol>
                <p>{t('woocommerce.checkoutPlugins.inline.note')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 p-4 dark:border-slate-700">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="space-y-2">
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                {t('woocommerce.checkoutPlugins.funnel.title')}
              </p>
              <p className="text-muted-foreground text-xs">
                {t('woocommerce.checkoutPlugins.funnel.description')}
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <span className="inline-flex items-center gap-1.5 rounded-md bg-violet-50 px-2 py-1 text-[11px] font-medium text-violet-800 dark:bg-violet-950/40 dark:text-violet-300">
                  <CreditCard className="h-3 w-3" />
                  {t('woocommerce.setupGuide.gateway.title')}
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2 py-1 text-[11px] font-medium text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                  <ArrowRightLeft className="h-3 w-3" />
                  {t('woocommerce.setupGuide.redirect.title')}
                </span>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5"
              onClick={() => navigate(storeRoutes.FUNNELS())}
            >
              {t('woocommerce.checkoutPlugins.funnel.goToFunnels')}
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
